/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/BulkImportPreview/BulkImportPreview.tsx.
 */

import React, { useEffect, useMemo, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import Button from "../../../../components/common/Button";
import ConfirmModal from "../../../../components/common/ConfirmModal";
import DataTable, { type Column } from "../../../../components/common/DataTable";
import "../Contributor.css";
import "./BulkImportPreview.css";
import type { BulkPreview, BulkRecordDetail } from "../DataInput/components/BulkImportModal";
import { resolveBulkRecordKey } from "../DataInput/bulkImportRecordKey";
import BulkRecordDetailModal from "./BulkRecordDetailModal";

type BulkCommit = {
  success: boolean;
  created?: string[];
  failed?: Array<{ code?: string; submission_code?: string; recordKey?: string; message: string }>;
  message?: string;
};

function apiPath(pathWithApiPrefix: string, pathWithoutApiPrefix: string) {
  const baseURL = String((axiosClient.defaults as any)?.baseURL || "");
  const normalized = baseURL.replace(/\/+$/, "");
  const endsWithApi = normalized.endsWith("/api");
  return endsWithApi ? pathWithoutApiPrefix : pathWithApiPrefix;
}

function normalizeBackendMessage(message: string): string {
  return String(message || "")
    .replace(/\bSubmission code\b/gi, "Record key")
    .replace(/\bsubmission code\b/gi, "record key");
}

function summarizeRejections(
  rejected: Array<{ stage?: string; field?: string; message?: string }>
) {
  const keyOf = (r: { stage?: string; field?: string; message?: string }) =>
    `${r.stage ?? ""}||${r.field ?? ""}||${String(r.message ?? "").trim()}`;

  const counts = new Map<string, number>();
  for (const r of rejected) {
    const k = keyOf(r);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }

  const top = Array.from(counts.entries())
    .map(([k, count]) => {
      const [stage, field, message] = k.split("||");
      return { count, stage: stage || "-", field: field || "-", message: message || "-" };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);

  return { totalRejected: rejected.length, top };
}

function topRejectionLine(
  rejected: Array<{ stage?: string; field?: string; message?: string }>
) {
  const top = summarizeRejections(rejected).top[0];
  if (!top) return null;
  return `${top.stage}.${top.field}: ${top.message} (${top.count})`;
}

/** React component — Bulk Import Preview Screen. */
export default function BulkImportPreviewScreen({
  preview,
  onBack,
}: {
  preview: BulkPreview | null;
  onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commit, setCommit] = useState<BulkCommit | null>(null);
  const [showConfirmImport, setShowConfirmImport] = useState(false);
  const [showConfirmBack, setShowConfirmBack] = useState(false);
  const [showImportSummary, setShowImportSummary] = useState(false);
  const [detailRecord, setDetailRecord] = useState<{
    recordKey: string;
    materialName: string;
    subMaterialName: string;
  } | null>(null);

  const hasCommitted = Boolean(commit);
  const rejectedCount = preview?.rejectedCount ?? preview?.rejected?.length ?? 0;
  const acceptedCount = preview?.acceptedCount ?? preview?.accepted?.length ?? 0;
  const hasRejections = rejectedCount > 0;
  const canCommit = Boolean(preview?.importId) && acceptedCount > 0 && !busy && !hasCommitted;

  useEffect(() => {
    if (!preview?.importId) return;
    // Always log a concise reason when there are rejections (helps debug in any env).
    if ((preview.rejected?.length || 0) > 0) {
      const rejected = preview.rejected || [];
      // eslint-disable-next-line no-console
      console.warn("[bulk-import] preview:rejected", {
        importId: preview.importId,
        totalSubmissions: preview.totalSubmissions,
        acceptedCount,
        rejectedCount,
      });
      const summary = summarizeRejections(rejected);
      // eslint-disable-next-line no-console
      console.groupCollapsed?.("[bulk-import] preview:rejections (top reasons)");
      // eslint-disable-next-line no-console
      console.table?.(summary.top);
      // eslint-disable-next-line no-console
      console.log("[bulk-import] preview:rejections sample", rejected.slice(0, 10));
      // eslint-disable-next-line no-console
      console.groupEnd?.();
    }
  }, [preview?.importId, preview?.totalSubmissions, preview?.rejected, acceptedCount, rejectedCount]);

  const rejectedRows = useMemo(() => preview?.rejected || [], [preview?.rejected]);
  const acceptedRows = useMemo(() => preview?.accepted || [], [preview?.accepted]);

  const acceptedTableData = useMemo(() => {
    return acceptedRows.map((raw, idx) => {
      const recordKey = resolveBulkRecordKey(raw);
      if (typeof raw === "string") {
        return { index: idx + 1, recordKey, materialName: "—", subMaterialName: "—" };
      }
      const materialName =
        raw.material_name ||
        raw.materialName ||
        raw.material_code ||
        raw.materialCode ||
        "—";
      const subMaterialName =
        raw.sub_material_name ||
        raw.subMaterialName ||
        raw.sub_material_code ||
        raw.subMaterialCode ||
        "—";

      return { index: idx + 1, recordKey, materialName, subMaterialName };
    });
  }, [acceptedRows]);

  type AcceptedTableRow = {
    index: number;
    recordKey: string;
    materialName: string;
    subMaterialName: string;
  };

  const acceptedColumns: Column<AcceptedTableRow>[] = useMemo(
    () => [
      { key: "index", label: "No." },
      { key: "recordKey", label: "Record Key" },
      { key: "materialName", label: "Material" },
      { key: "subMaterialName", label: "Sub Material" },
      {
        key: "actions",
        label: "Details",
        align: "center",
        render: (item) => (
          <button
            type="button"
            className="bulk-preview-link-btn"
            onClick={() =>
              setDetailRecord({
                recordKey: item.recordKey,
                materialName: item.materialName,
                subMaterialName: item.subMaterialName,
              })
            }
          >
            View details
          </button>
        ),
      },
    ],
    []
  );

  const detailMap = useMemo<Record<string, BulkRecordDetail>>(
    () => preview?.details || {},
    [preview?.details]
  );

  type RejectedRow = {
    recordKey: string;
    stage: string;
    row: number | string;
    field: string;
    message: string;
  };

  const rejectedTableData = useMemo<RejectedRow[]>(() => {
    return rejectedRows.map((r) => ({
      recordKey: resolveBulkRecordKey(r),
      stage: String(r.stage || ""),
      row: r.row ?? "",
      field: String(r.field || ""),
      message: String(r.message || ""),
    }));
  }, [rejectedRows]);

  const rejectedColumns: Column<RejectedRow>[] = useMemo(
    () => [
      { key: "recordKey", label: "Record Key" },
      { key: "stage", label: "Stage" },
      { key: "row", label: "Row" },
      { key: "field", label: "Field" },
      { key: "message", label: "Message" },
    ],
    []
  );

  type CommitFailedRow = { recordKey: string; reason: string };
  const commitFailedRows = useMemo<CommitFailedRow[]>(() => {
    const failed = commit?.failed || [];
    return failed.map((f) => ({
      recordKey: resolveBulkRecordKey(f),
      reason: String(f.message || ""),
    }));
  }, [commit]);

  const commitFailedColumns: Column<CommitFailedRow>[] = useMemo(
    () => [
      { key: "recordKey", label: "Record Key" },
      { key: "reason", label: "Reason" },
    ],
    []
  );

  const handleCommit = async () => {
    if (!preview?.importId) return;
    setError(null);
    setCommit(null);

    try {
      setBusy(true);
      const commitUrl = apiPath(
        "/api/import/submissions/commit",
        "/import/submissions/commit"
      );

      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("[bulk-import] commit:start", {
          importId: preview.importId,
          acceptedCount,
          rejectedCount,
          hasRejections,
          commitUrl,
        });
      }

      const res = await axiosClient.post(commitUrl, { importId: preview.importId });

      if (!res?.data) throw new Error("Import failed.");

      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("[bulk-import] commit:response", res.data);
      }

      setCommit({
        success: Boolean(res.data.success),
        created: res.data.created || [],
        failed: (res.data.failed || []).map(
          (f: { submission_code?: string; recordKey?: string; message: string }) => ({
            ...f,
            message: normalizeBackendMessage(f?.message),
          })
        ),
        message: normalizeBackendMessage(res.data.message),
      });
      setShowImportSummary(true);

      if (process.env.NODE_ENV !== "production" && res?.data && res.data.success === false) {
        // eslint-disable-next-line no-console
        console.error("[bulk-import] commit:failed", {
          message: res.data.message,
          failed: res.data.failed,
        });
      }
    } catch (e: any) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[bulk-import] commit:error", {
          message: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
      }
      setError(
        normalizeBackendMessage(
          e?.response?.data?.message || e?.message || "Import failed."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  if (!preview) {
    return (
      <div className="bulk-preview-page">
        <AnalysisBanner title="Bulk Import – Preview" />
        <div className="bulk-preview-card">
          <div className="bulk-preview-empty">
            No preview data found. Please upload a file again.
          </div>
          <div className="bulk-preview-actions">
            <Button label="Back" onClick={onBack} type="button" variant="default" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-preview-page">
      <AnalysisBanner title="Bulk Import – Preview" />

      <div className="bulk-preview-card">
        <div className="bulk-preview-top">
          <div className="bulk-preview-meta">
            <div className="bulk-preview-meta-item">
              <div className="bulk-preview-meta-label">Import ID</div>
              <div className="bulk-preview-meta-value">{preview.importId}</div>
            </div>
            <div className="bulk-preview-meta-item">
              <div className="bulk-preview-meta-label">Total</div>
              <div className="bulk-preview-meta-value">
                {preview.totalSubmissions ?? "-"}
              </div>
            </div>
            <div className="bulk-preview-meta-item">
              <div className="bulk-preview-meta-label">Accepted</div>
              <div className="bulk-preview-meta-value">{acceptedCount}</div>
            </div>
            <div className="bulk-preview-meta-item">
              <div className="bulk-preview-meta-label">Rejected</div>
              <div className="bulk-preview-meta-value">{rejectedCount}</div>
            </div>
          </div>

          <div className="bulk-preview-buttons">
            <Button
              label="Back"
              onClick={() => setShowConfirmBack(true)}
              type="button"
              variant="default"
              disabled={busy}
            />
          </div>
        </div>

        <div className="bulk-preview-section">
          <DataTable
            embedded
            stickyHeader
            wrapperMaxHeight={720}
            data={acceptedTableData}
            columns={acceptedColumns}
            loading={false}
            title={`Accepted Records (${acceptedCount})`}
            searchPlaceholder="Search record key"
            pagination={false}
            emptyMessage="No accepted records."
          />
        </div>

        {hasRejections && (
          <div className="bulk-preview-warning">
            Fix rejected rows and re-upload.
            {acceptedCount > 0 ? " You can still import the accepted records below." : ""}
            {rejectedRows.length > 0 && (
              <div style={{ marginTop: 6, fontSize: "0.9em" }}>
                Top issue: <strong>{topRejectionLine(rejectedRows) || "—"}</strong>
              </div>
            )}
          </div>
        )}

        {error && <div className="bulk-preview-error">{error}</div>}

        {hasRejections && (
          <div className="bulk-preview-section">
            <DataTable
              embedded
              stickyHeader
              wrapperMaxHeight={360}
              data={rejectedTableData}
              columns={rejectedColumns}
              loading={false}
              title={`Rejected Records (${rejectedCount})`}
              searchPlaceholder=""
              pagination={false}
              emptyMessage="No rejected records."
            />
          </div>
        )}

        <div className="bulk-preview-footer">
          {!hasCommitted && (
            <Button
              label={busy ? "Importing..." : "Confirm Import"}
              onClick={() => setShowConfirmImport(true)}
              disabled={!canCommit}
              type="button"
              variant="default"
            />
          )}
        </div>
      </div>

      {commit && showImportSummary && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-card fade-in bulk-summary-modal">
            <div className="modal-header">
              <h2>Import Summary</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowImportSummary(false);
                  onBack();
                }}
                disabled={busy}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body bulk-summary-modal__body">
              {commit.message && <div className="bulk-preview-commit-msg">{commit.message}</div>}

              <div className="bulk-preview-commit-grid">
                <div className="bulk-preview-commit-box">
                  <div className="bulk-preview-commit-label">Created</div>
                  <div className="bulk-preview-commit-value">{commit.created?.length || 0}</div>
                </div>
                <div className="bulk-preview-commit-box">
                  <div className="bulk-preview-commit-label">Failed</div>
                  <div className="bulk-preview-commit-value">{commit.failed?.length || 0}</div>
                </div>
              </div>

              {(commit.failed?.length || 0) > 0 && (
                <div className="bulk-preview-commit-failed">
                  <DataTable
                    embedded
                    stickyHeader
                    wrapperMaxHeight={320}
                    data={commitFailedRows}
                    columns={commitFailedColumns}
                    loading={false}
                    title={`Failed Records (${commit.failed?.length || 0})`}
                    searchPlaceholder="Search record key or reason"
                    pagination={false}
                    emptyMessage="No failed records."
                  />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <Button
                label="Close"
                onClick={() => {
                  setShowImportSummary(false);
                  onBack();
                }}
                disabled={busy}
                type="button"
                variant="default"
              />
            </div>
          </div>
        </div>
      )}

      <BulkRecordDetailModal
        open={Boolean(detailRecord)}
        recordKey={detailRecord?.recordKey || ""}
        materialName={detailRecord?.materialName}
        subMaterialName={detailRecord?.subMaterialName}
        detail={detailRecord ? detailMap[detailRecord.recordKey] ?? null : null}
        onClose={() => setDetailRecord(null)}
      />

      <ConfirmModal
        open={showConfirmImport && !hasCommitted}
        title="Confirm import?"
        description="This will import all accepted records from the uploaded Excel. Do you want to continue?"
        onCancel={() => setShowConfirmImport(false)}
        onConfirm={async () => {
          setShowConfirmImport(false);
          await handleCommit();
        }}
        cancelText="Cancel"
        confirmText="Yes, import"
        loading={busy}
        confirmButtonVariant="primary"
      />

      <ConfirmModal
        open={showConfirmBack}
        title="Leave this preview?"
        description="If you go back now, you will lose this preview session and will need to upload the Excel again. Do you want to continue?"
        onCancel={() => setShowConfirmBack(false)}
        onConfirm={() => {
          setShowConfirmBack(false);
          onBack();
        }}
        cancelText="Stay"
        confirmText="Yes, go back"
        loading={busy}
        confirmButtonVariant="danger"
      />
    </div>
  );
}

